import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Divider,
  Fade,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { buildCompanyDescriptionPrompt } from '../../../utils/companyPrompts';

const FinalizeContainer = styled(Box)(({ theme }) => ({
  width: '100%',
}));

const SummaryCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
}));

const DetailRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(1, 0),
  '&:not(:last-child)': {
    borderBottom: `1px solid ${theme.palette.divider}`,
  }
}));

interface CompanyFinalizeStepProps {
  formData: any;
  updateFormData: (field: string, value: string) => void;
  onSubmit: () => void;
  onBack: () => void;
  loading: boolean;
  appName: string;
  statusText?: string;
  chatApi?: any;
}

export default function CompanyFinalizeStep({ 
  formData, 
  updateFormData, 
  onSubmit, 
  onBack, 
  loading,
  appName,
  statusText,
  chatApi 
}: CompanyFinalizeStepProps) {
  const [generatingDescription, setGeneratingDescription] = useState(false);
  const [hasGeneratedDescription, setHasGeneratedDescription] = useState(false);

  // Remove auto-generation - now only manual generation via button

  const generateDescription = async () => {
    if (!formData.companyName || !formData.industry || !formData.website || !chatApi) return;
    
    setGeneratingDescription(true);
    try {
      // A website is guaranteed here — the guard above returns early without one.
      const prompt = buildCompanyDescriptionPrompt({
        name: formData.companyName,
        industry: formData.industry,
        website: formData.website,
      });
      const description = await chatApi.syncCompletion(prompt, true);
      updateFormData('description', description);
      setHasGeneratedDescription(true);
    } catch (error) {
      console.error('Error generating description:', error);
      // Fallback to simple description if API fails
      const fallbackDescription = `${formData.companyName} is a ${formData.industry.toLowerCase()} company${formData.size ? ` with ${formData.size} employees` : ''}${formData.location ? ` based in ${formData.location}` : ''}. We are committed to delivering exceptional products and services to our customers.`;
      updateFormData('description', fallbackDescription);
      setHasGeneratedDescription(true);
    } finally {
      setGeneratingDescription(false);
    }
  };

  const handleSubmit = () => {
    if (!loading) {
      onSubmit();
    }
  };

  return (
    <FinalizeContainer>
      <Fade in={true} timeout={800}>
        <Box>
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600, 
                mb: 2,
                color: 'text.primary'
              }}
            >
              Almost ready!
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ lineHeight: 1.6 }}
            >
              Review your company information and add a description to complete setup
            </Typography>
          </Box>

          <SummaryCard elevation={0}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
              Company Overview
            </Typography>
            
            <DetailRow>
              <Typography variant="body2" color="text.secondary">Company Name</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{formData.companyName}</Typography>
            </DetailRow>
            
            <DetailRow>
              <Typography variant="body2" color="text.secondary">Industry</Typography>
              <Chip 
                label={formData.industry} 
                size="small" 
                color="primary" 
                variant="outlined"
              />
            </DetailRow>
            
            <DetailRow>
              <Typography variant="body2" color="text.secondary">Website</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{formData.website}</Typography>
            </DetailRow>
            
            <DetailRow>
              <Typography variant="body2" color="text.secondary">Company Size</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{formData.size}</Typography>
            </DetailRow>
            
            <DetailRow>
              <Typography variant="body2" color="text.secondary">Location</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{formData.location}</Typography>
            </DetailRow>
            
            {formData.logoUrl && (
              <DetailRow>
                <Typography variant="body2" color="text.secondary">Logo</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>✓ Provided</Typography>
              </DetailRow>
            )}
          </SummaryCard>

          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                Company Description
              </Typography>
              {!generatingDescription && formData.companyName && formData.industry && formData.website && chatApi && (
                <Button
                  size="small"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={generateDescription}
                  sx={{ ml: 2, textTransform: 'none' }}
                  variant="outlined"
                >
                  Generate Description
                </Button>
              )}
            </Box>
            
            <TextField
              label="Company Description"
              value={formData.description}
              onChange={(e) => updateFormData('description', e.target.value)}
              multiline
              rows={4}
              fullWidth
              placeholder="Tell us about your company, what you do, and your mission... or click 'Generate Description' to create one automatically"
              helperText="This helps us understand your business and provide better AI assistance. You can write it manually or generate it from your website."
              disabled={generatingDescription}
              InputProps={{
                endAdornment: generatingDescription ? (
                  <CircularProgress size={20} sx={{ position: 'absolute', right: 12, top: 12 }} />
                ) : null,
              }}
            />
            
            {generatingDescription && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AutoAwesomeIcon sx={{ mr: 1 }} />
                  Reading your website and generating company description...
                </Box>
              </Alert>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4, flexDirection: 'column', alignItems: 'center' }}>
            {statusText && loading && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {statusText}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={onBack}
                disabled={loading}
                sx={{ px: 3 }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading || !formData.description?.trim()}
                sx={{ 
                  px: 4,
                  fontWeight: 600,
                  textTransform: 'none',
                  position: 'relative'
                }}
              >
                {loading && (
                  <CircularProgress
                    size={20}
                    sx={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      marginLeft: '-10px',
                      marginTop: '-10px',
                    }}
                  />
                )}
                {loading ? 'Creating Company...' : 'Create Company'}
              </Button>
            </Box>
          </Box>

          {!formData.description?.trim() && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Please add a company description to continue. You can write one manually or use the "Generate Description" button above.
            </Alert>
          )}
        </Box>
      </Fade>
    </FinalizeContainer>
  );
}